import { GroupData, GroupUserData, GroupViewer, GroupViewerMode } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MeetingRoomAudienceType, MeetingRoomStatus, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { closeMeetingActivityRoom, openMeetingActivityRoom, startRoomHighlight } from "../../../helpers/meeting";
import { sdkClient } from "../../../helpers/sdk";
import { toastError, toastSuccess } from "../../../helpers/toast";
import { useSocketAppDispatch } from "../../../hooks/app";
import { useMeetingSelector } from "../../../hooks/meeting";
import { useGroupsToEscaetGroups, useUsersToEscaetUsers } from "../../../hooks/meetingRoom";
import { PopulatedMeetingRooms, PopulatedMeetingUser } from "../../../interfaces/populatedData";
import { ExpertContextState } from "../../../pages/master/expert/context";
import socketUserMeetingRoomsActions from "../../../store/meeting-room/actions";
import { ContainerUtils } from "../../../helpers/container";

export interface GroupViewerExpertContainerProps {
  meetingContainerAPI: MeetingContainerAPI | undefined; 
  context?: ExpertContextState;
  users: PopulatedMeetingUser[];
  rooms: PopulatedMeetingRooms;
  session: MeetingSession;
  selfUserId: string;
  validators?: {
    onJoinGroupAnonymously?: (group: GroupData) => boolean;
    onJoinGroup?: (group: GroupData) => boolean;
    onSendGroupToBoard?: (group: GroupData) => boolean;
    onTalkToGroupLeader?: (group: GroupData) => boolean;
    onTalkToAllGroupLeaders?: () => boolean;
    onTalkToAllGroupUsers?: () => boolean;
    onEndGroupDiscussion?: () => boolean;
    onEndTalkMode?: () => boolean;
    onMoveUserToGroup?: (user: GroupUserData, groupFrom: GroupData | undefined, groupTo: GroupData | undefined) => boolean;
    onSetUserAsLeader?: (user: GroupUserData, group: GroupData) => boolean;
  }
  style?: React.CSSProperties;
  className?: string;
}

export const GroupViewerExpertContainer = ({
  meetingContainerAPI,
  context,
  users,
  rooms,
  session,
  selfUserId,
  validators,
  style,
  className
}: GroupViewerExpertContainerProps) => {
  const dispatch = useSocketAppDispatch();

  const [meetingId] = useMeetingSelector(meetingContainerAPI, state => state.meeting?.id);

  const escaetUsers = useUsersToEscaetUsers(users, selfUserId);
  const escaetGroups = useGroupsToEscaetGroups(rooms.groups.filter(group => group.room.status === MeetingRoomStatus.Ongoing));

  const onMoveUserToGroup = async (
    user: GroupUserData, 
    groupFrom: GroupData | undefined, 
    groupTo: GroupData | undefined,
  ) => {
    if (validators?.onMoveUserToGroup && !validators.onMoveUserToGroup(user, groupFrom, groupTo)) return;
    if (groupFrom) {
      const result = await sdkClient.getWebsocketClient().emitter.meeting.delistFromRoom({
        userId: user.id,
        roomId: groupFrom.id,
        force: true,
      });
      if (!result || result.error) {
        toastError("Le groupe de l'utilisateur n'a pas pu être modifié");
        return;
      }
    }
    if (groupTo) {
      await sdkClient.getWebsocketClient().emitter.meeting.enlistInRoom({
        userId: user.id,
        roomId: groupTo.id,
        force: true,
      });
    }
    toastSuccess(`${user.firstName} a été déplacé du groupe ${groupFrom?.title} vers le groupe ${groupTo?.title}`);
  };

  const onSetUserAsLeader = async (
    user: GroupUserData,
    group: GroupData,
  ) => {
    if (validators?.onSetUserAsLeader && !validators.onSetUserAsLeader(user, group)) return;
    const result = await sdkClient.getWebsocketClient().emitter.meeting.updateRoomLeader({
      roomId: group.id,
      userId: user.id,
    });
    if (!result || result.error) {
      toastError("Le leader du groupe n'a pas pu être modifié");
      return;
    }
  };

  const onCloseGroups = async () => {
    // In case there is multiple ongoing batches (not supposed to happen)
    const batchesIds = new Set<string>();
    // In case there is lone groups (not supposed to happen)
    const loneGroupIds = new Set<string>();
    rooms.groups.forEach(group => {
      if (group.room.status === MeetingRoomStatus.Closed) return;
      if (group.room.batchId) {
        batchesIds.add(group.room.batchId);
      } else {
        loneGroupIds.add(group.id);
      }
    });
    Array.from(batchesIds).forEach(batchId => dispatch(socketUserMeetingRoomsActions.updateBatch({
      batchId,
      status: MeetingRoomStatus.Closed,
    })));
    Array.from(loneGroupIds).forEach(groupId => sdkClient.getWebsocketClient().emitter.meeting.updateRoom({
      roomId: groupId,
      status: MeetingRoomStatus.Closed,
    }));
  }

  const onSendGroupToBoard = async (group: GroupData) => {
    if (validators?.onSendGroupToBoard && !validators.onSendGroupToBoard(group)) return;
    startRoomHighlight(group.id);
  };

  const onJoinGroupAnonymously = async (group: GroupData) => {
    if (validators?.onJoinGroupAnonymously && !validators.onJoinGroupAnonymously(group)) return;
    context?.setSpiedGroupId(group.id);
  };

  const onJoinGroup = async (group: GroupData) => {
    if (validators?.onJoinGroup && !validators.onJoinGroup(group)) return;
    if (!meetingId) return;
    openMeetingActivityRoom({
      id: meetingId,
      audience: {
        type: MeetingRoomAudienceType.RoomMember,
        values: [group.id],
      },
    });
  }

  const onTalkToLeader = async (group: GroupData) => {
    if (validators?.onTalkToGroupLeader && !validators.onTalkToGroupLeader(group)) return;
    if (!meetingId) return;
    openMeetingActivityRoom({
      id: meetingId,
      audience: {
        type: MeetingRoomAudienceType.RoomLeader,
        values: [group.id],
      },
    });
  }

  const onTalkToAllLeaders = async () => {
    if (validators?.onTalkToAllGroupLeaders && !validators.onTalkToAllGroupLeaders()) return;
    if (!meetingId) return;
    openMeetingActivityRoom({
      id: meetingId,
      audience: {
        type: MeetingRoomAudienceType.RoomLeader,
        values: "*",
      },
    });
  }

  const onTalkToEveryone = async () => {
    if (validators?.onTalkToAllGroupUsers && !validators.onTalkToAllGroupUsers()) return;
    if (!meetingContainerAPI) return;
    ContainerUtils.setMeetingSessionHeardByEveryoneState(meetingContainerAPI, session.id, true);
  }

  const onEndTalkMode = async () => {
    if (validators?.onEndTalkMode && !validators.onEndTalkMode()) return;
    if (!meetingContainerAPI) return;
    if (session.canBeHeardByEveryone) {
      ContainerUtils.setMeetingSessionHeardByEveryoneState(meetingContainerAPI, session.id, false);
    }
    if (rooms.activityRoom?.room.status !== MeetingRoomStatus.Closed && meetingId) {
      closeMeetingActivityRoom(meetingId);
    }
  }

  const viewerMode: GroupViewerMode = 
    session.canBeHeardByEveryone ? GroupViewerMode.TalkToUsers : GroupViewerMode.Normal;

  return (
    <GroupViewer 
      style={{
        color: "#222222",
        ...(style ?? {})
      }}
      className={className}
      users={escaetUsers}
      groups={escaetGroups}
      mode={viewerMode}
      onMoveUserToGroup={onMoveUserToGroup}
      onEndGroupDiscussion={onCloseGroups}
      onSetUserAsLeader={onSetUserAsLeader}
      onSendGroupToBoard={onSendGroupToBoard}
      onJoinGroup={onJoinGroup}
      onJoinGroupAnonymously={onJoinGroupAnonymously}
      onTalkToGroupLeader={onTalkToLeader}
      onTalkToAllGroupLeaders={onTalkToAllLeaders}
      onTalkToAllGroupUsers={onTalkToEveryone}
      onEndTalkMode={onEndTalkMode}
    />
  );
}